/**
 * Pure geometry for the screen-centre aiming reticle in hud.ts (live ask:
 * "show a small cross-hair in the middle of the screen ... like Valorant or
 * any comp game"). Split out of hud.ts the same way minimap-geometry.ts was
 * split out of drawRadar — a directly testable surface with no DOM/canvas
 * involved, so "is it centred and symmetric" is a numeric assertion, not an
 * eyeballed screenshot.
 *
 * ── Why four separate rects, not one clipped cross ─────────────────────────
 *
 * The previous shape was two full-length bars with a CSS clip-path carved out
 * of the middle — visually similar, but the "four short ticks pulled away
 * from a centre gap" read (the defining Valorant/CS shape, as opposed to a
 * generic HUD "+") comes out cleaner as four independently-sized rectangles.
 * hud.ts positions each one with a plain `left/top/width/height` in CSS
 * pixels, in a coordinate space whose origin (0,0) is DEAD SCREEN-CENTRE —
 * hud.ts gets there with the ordinary `left:50%; top:50%;
 * transform:translate(-50%,-50%)` trick on a zero-size wrapper, so these
 * numbers never need to know the viewport size at all. That is *why* the
 * reticle cannot drift with viewport/aspect: the centring is a CSS percentage,
 * not a computed pixel offset, and nothing here ever reads window dimensions.
 * `crosshairCentre` below exists only to pin that contract as an explicit,
 * tested invariant instead of an assumption.
 *
 * ── Sizing ───────────────────────────────────────────────────────────────
 *
 * Three presets (Small/Medium/Large), each with hairline thickness (1-2px —
 * "crisp 1-2px lines", never a thick bar) and a bigger centre gap than the
 * old shape, which is what makes it read as a competitive-shooter crosshair
 * instead of a broadcast HUD marker.
 */

/** A CSS box in local px, relative to the reticle's own centre (0,0). */
export interface CrosshairBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface CrosshairSizePreset {
  readonly label: string;
  /** Distance from dead centre to the NEAR edge of each tick, px — the
   *  visible "hole" in the middle is 2x this. This is the number that turns
   *  a generic "+" into the gapped Valorant shape. */
  readonly gap: number;
  /** Tick length, px (centre-to-far-edge is `gap + length`). */
  readonly length: number;
  /** Tick thickness, px. Kept to 1-2px per the brief — a thick crosshair
   *  reads as a HUD reticle, not a competitive one. */
  readonly thickness: number;
  /** Ball-destination dot diameter, px, at charge = 0. hud.ts grows this
   *  live via a CSS transform (unchanged behaviour) — this is only the base. */
  readonly dot: number;
}

/** Small / Medium (default) / Large — mirrors the settings screen's other
 *  discrete choices (BALL_FOCUS_CHOICES etc. in settings.ts). */
export const CROSSHAIR_SIZE_PRESETS: readonly CrosshairSizePreset[] = [
  { label: 'Small', gap: 3, length: 4, thickness: 1, dot: 3 },
  { label: 'Medium', gap: 4, length: 6, thickness: 2, dot: 4 },
  { label: 'Large', gap: 6, length: 9, thickness: 2, dot: 6 },
];

/** Indices into CROSSHAIR_SIZE_PRESETS — the settings blob stores one of these. */
export const CROSSHAIR_SIZE_CHOICES: readonly number[] = [0, 1, 2];

/** Medium — visually close to the old shape's weight, just tighter/crisper. */
export const DEFAULT_CROSSHAIR_SIZE_INDEX = 1;

/** Clamp an untrusted/out-of-range size index to a valid preset — defensive
 *  fallback for hud.ts (HudState is a plain mutable object with no validator
 *  of its own; settings.ts's parseSettings is the REAL gate, this is a second
 *  line of defence so a bad number can never index `undefined`). */
export function clampCrosshairSizeIndex(index: number): number {
  const i = Math.round(index);
  if (!Number.isFinite(i)) return DEFAULT_CROSSHAIR_SIZE_INDEX;
  return Math.max(0, Math.min(CROSSHAIR_SIZE_PRESETS.length - 1, i));
}

export interface CrosshairTicks {
  top: CrosshairBox;
  bottom: CrosshairBox;
  left: CrosshairBox;
  right: CrosshairBox;
}

/**
 * The four tick rectangles for one size preset, centred on (0,0). Reflecting
 * `top` across y=0 must land exactly on `bottom` (and `left` across x=0 on
 * `right`) — that symmetry is what crosshair-geometry.test.ts pins, because a
 * crosshair that is even one pixel off-centre on one arm reads as broken
 * immediately (unlike almost anything else in the HUD).
 */
export function crosshairTicks(preset: CrosshairSizePreset): CrosshairTicks {
  const { gap, length, thickness } = preset;
  const half = thickness / 2;
  return {
    top: { left: -half, top: -(gap + length), width: thickness, height: length },
    bottom: { left: -half, top: gap, width: thickness, height: length },
    left: { left: -(gap + length), top: -half, width: length, height: thickness },
    right: { left: gap, top: -half, width: length, height: thickness },
  };
}

/** The ball-destination dot's BASE box (charge = 0), centred on (0,0). hud.ts
 *  applies the live charge growth on top via `transform: scale(...)` — same
 *  mechanism as before, unaffected by which size preset is active. */
export function crosshairDotBox(preset: CrosshairSizePreset): CrosshairBox {
  const half = preset.dot / 2;
  return { left: -half, top: -half, width: preset.dot, height: preset.dot };
}

/**
 * The screen-space point the reticle sits at for a given viewport size. This
 * is exactly what hud.ts's CSS (`left:50%; top:50%;
 * transform:translate(-50%,-50%)`) computes — expressed here as a pure
 * function purely so "stays dead centre for any viewport size/aspect" is a
 * checked invariant rather than an assumption nobody can re-verify later.
 */
export function crosshairCentre(viewportWidth: number, viewportHeight: number): { x: number; y: number } {
  return { x: viewportWidth / 2, y: viewportHeight / 2 };
}
